import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Checkbox,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import TextSearch from '../../controls/TextSearch';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useState } from 'react';
import { fetchWorkQueuesAsync } from '../../../data/monitoring.slice';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as monitoringSlice from '../../../data/monitoring.slice';
import { Workflow, WorkflowState } from '../../../data/models/workflow.model';
import {
  DataGridPro,
  GridColDef,
  GridPaginationModel,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import FormControl from '@mui/material/FormControl';
import { capitalize } from 'lodash';
import AddWorkFlowDialog from './AddWorkflowDialog';
import { hasWordStartingWith } from '../../../utils/string.utils';
import { useCanCreateWorkFlow } from '../../../hooks/permissions/useCanCreateWorkflow';
import { useNavigate } from 'react-router-dom';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'Workflows';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  menuContainer: `${PREFIX}-menuContainer`,
  queueSelect: `${PREFIX}-queueSelect`,
  grid: `${PREFIX}-grid`,
  statusSelect: `${PREFIX}-statusSelect`,
  statusLabel: `${PREFIX}-statusLabel`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`& .${classes.container}`]: {
    padding: theme.spacing(3),
  },
  [`& .${classes.header}`]: {
    paddingBottom: theme.spacing(3),
    marginBottom: theme.spacing(1),
    justifyContent: 'space-between',
  },
  [`& .${classes.menuContainer}`]: {
    justifyContent: 'space-between',
  },
  [`& .${classes.queueSelect}`]: {
    width: 282,
  },
  [`& .${classes.grid}`]: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    minWidth: 0,
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
    '& .MuiDataGrid-row:hover': {
      cursor: 'pointer',
    },
  },
  [`& .${classes.statusSelect}`]: {
    width: 282,
  },
  [`& .${classes.statusLabel}`]: {
    background: 'white',
    paddingRight: 5,
  },
}));

export default function WorkFlows() {
  const defaultPageSize = 20;
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const isSuperReaderUser = useIsSuperReaderUser();
  const canCreateWorkflow = useCanCreateWorkFlow();
  const dispatch = useAppDispatch();
  const workQueues = useAppSelector(monitoringSlice.selectWorkQueues);
  const workflows = useAppSelector(monitoringSlice.selectWorkflows);
  const isLoading = useAppSelector(monitoringSlice.selectLoadingWorkflows);
  const [searchText, setSearchText] = useState('');
  const [workQueueUUID, setWorkQueueUUID] = useState('');
  const [addWorkflowDialogOpen, setAddWorkflowDialogOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: defaultPageSize,
  });
  const [statuses, setStatuses] = useState<string[]>([
    WorkflowState.Draft,
    WorkflowState.Active,
  ]);
  const selectableStatuses = [
    WorkflowState.Draft,
    WorkflowState.Active,
    WorkflowState.Inactive,
  ];

  let filteredWorkflows = workflows;
  if (workflows && searchText) {
    filteredWorkflows = workflows.filter(
      (workflow) =>
        hasWordStartingWith(workflow.name, searchText) ||
        (workflow.description &&
          hasWordStartingWith(workflow.description, searchText)),
    );
  }

  const fetchWorkflows = (workQueueUUID: string, statuses: WorkflowState[]) => {
    if (!statuses || statuses.length === 0) {
      dispatch(monitoringSlice.setWorkflows([]));
      return;
    }
    dispatch(monitoringSlice.fetchWorkflowsAsync(workQueueUUID, statuses));
  };

  useEffect(() => {
    dispatch(monitoringSlice.setWorkflows([]));
    dispatch(fetchWorkQueuesAsync());
  }, []);

  const handleWorkQueueChange = (workQueueUUID: string) => {
    setWorkQueueUUID(workQueueUUID);
    fetchWorkflows(workQueueUUID, statuses as WorkflowState[]);
  };

  const handleSearchTextChange = (val: string) => {
    setSearchText(val);
  };

  const handlePaginationChange = (model: GridPaginationModel) => {
    setPaginationModel(model);
  };

  const handleStatusChange = (event: SelectChangeEvent) => {
    const {
      target: { value },
    } = event;
    const newStatuses = typeof value === 'string' ? value.split(',') : value;
    setStatuses(newStatuses);
    fetchWorkflows(workQueueUUID, newStatuses as WorkflowState[]);
  };

  const handleAddWorkflowClick = () => {
    dispatch(monitoringSlice.setDraftWorkflow(null));
    setAddWorkflowDialogOpen(true);
  };

  const handleAddWorkflowClose = () => {
    setAddWorkflowDialogOpen(false);
  };

  const handleAddWorkflowSave = (workQueueUUID: string) => {
    navigate(`/workflow/create?workQueueUUID=${workQueueUUID}`);
  };

  const handleWorkflowClick = (params: GridRowParams) => {
    dispatch(monitoringSlice.setDraftWorkflow(null));
    navigate(`/workflow/edit?workflowUUID=${params.row.workflowUUID}`);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('workflows.name'),
      sortable: true,
      minWidth: 250,
    },
    {
      field: 'description',
      headerName: t('workflows.description'),
      sortable: true,
      minWidth: 400,
      maxWidth: 600,
    },
    {
      field: 'state',
      headerName: t('workflows.status'),
      sortable: true,
      valueFormatter: (params) => capitalize(params.value),
    },
  ];

  return (
    <StyledPaper elevation={2}>
      <Stack direction="column" className={classes.container}>
        <Stack direction="row" className={classes.header}>
          <Typography variant="h2">{t('workflows.title')}</Typography>
          <FormControl className={classes.statusSelect}>
            <InputLabel
              id="select-status-label"
              className={classes.statusLabel}>
              {t('workflows.status')}
            </InputLabel>
            <Select
              labelId="select-status-label"
              multiple
              value={statuses as any}
              onChange={handleStatusChange}
              input={<OutlinedInput label="Tag" />}
              renderValue={(selected) =>
                (selected as any)
                  .map((val: string) => capitalize(val))
                  .join(', ')
              }>
              {selectableStatuses.map((status) => (
                <MenuItem key={status} value={status}>
                  <Checkbox checked={statuses.includes(status)} />
                  <ListItemText primary={capitalize(status)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Stack direction="row" className={classes.menuContainer}>
          <Stack direction="row" spacing={2}>
            <TextField
              className={classes.queueSelect}
              select
              label={t('workflows.workQueue')}
              variant="outlined"
              required={true}
              value={workQueueUUID}
              onChange={(evt) => handleWorkQueueChange(evt.target.value)}>
              {workQueues?.map((workQueue) => {
                return (
                  <MenuItem
                    value={workQueue.workQueueUUID ?? ''}
                    key={workQueue.workQueueUUID}>
                    {workQueue.name}
                  </MenuItem>
                );
              })}
            </TextField>
            {workflows && workflows.length > 0 && (
              <TextSearch
                id="text-search"
                icon={<SearchIcon />}
                label={t('workflows.search')}
                placeholder={t('workflows.searchPlaceholder')}
                currentSearchFilter={searchText}
                onSearchChange={handleSearchTextChange}></TextSearch>
            )}
          </Stack>
          {!isSuperReaderUser && (
            <Button
              color="primary"
              variant="contained"
              disabled={!workQueueUUID || !canCreateWorkflow}
              onClick={handleAddWorkflowClick}>
              {t('workflows.add')}
            </Button>
          )}
        </Stack>
        {filteredWorkflows && (
          <Box className={classes.grid}>
            <DataGridPro
              disableColumnMenu={true}
              getRowId={(row) => row.workflowUUID}
              rows={filteredWorkflows}
              columns={columns}
              rowCount={filteredWorkflows.length}
              loading={isLoading}
              pagination={true}
              paginationModel={paginationModel}
              pageSizeOptions={[10, 20, 30, 40, 50, 100]}
              onPaginationModelChange={handlePaginationChange}
              onRowClick={handleWorkflowClick}
              autoHeight
            />
          </Box>
        )}
        <AddWorkFlowDialog
          workQueueUUID={workQueueUUID}
          open={addWorkflowDialogOpen}
          close={handleAddWorkflowClose}
          save={handleAddWorkflowSave}
        />
      </Stack>
    </StyledPaper>
  );
}
